import { AfterViewInit, Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { LocalStorage } from 'angular-web-storage';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

import {
  ScheduledEvent
} from '../../../../../../../../../models/scheduled-event';
import { CallbackResult, dateStringFromISO8601 } from '../../../../../../../../../utils';
import { AddEventNoteModalComponent } from './modals/add-event-note-modal/add-event-note-modal.component';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { ListResponse } from '../../../../../../../../../models/list-response';
import { EventNote } from '../../../../../../../../../models/event';
import { EventService } from '../../../../../../../../../services/event/event.service';
import { EditEventNoteModalComponent } from './modals/edit-event-note-modal/edit-event-note-modal.component';


@Component({
  selector: 'app-event-notes-sub-list',
  templateUrl: './event-notes-sub-list.component.html',
  styleUrls: ['./event-notes-sub-list.component.css']
})
export class EventNotesSubListComponent implements OnInit, AfterViewInit {
  @Input() event: ScheduledEvent;
  @Input() resultCallback: (result: CallbackResult, reload: boolean) => void;

  @ViewChild('notesTableWrapper') notesTableWrapper;
  @ViewChild('notesTableTitleContainer') notesTableTitleContainer;

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other
  // similar variables.
  @LocalStorage() public pageSizeEventNotesSubList;

  public filtersChangedSubject = new Subject<null>();

  public notesList: ListResponse<EventNote>;

  public loadingNotes = true;

  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    private eventService: EventService,
    private orgService: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.pageSizeEventNotesSubList) {
      this.pageSizeEventNotesSubList = 10;
    }
    // This is needed in order for the variable to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeEventNotesSubList = this.pageSizeEventNotesSubList;

    // Handle changes to the filters by reloading the results,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadNotes();
    });

    this.loadNotes();
  }

  ngAfterViewInit() {
    this.updateTableTitleContainerWidth();
    // Initial call can make the title container a little too small - might be
    // related to the scrollbar?
    setTimeout(
      () => { this.updateTableTitleContainerWidth(); },
      100
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.updateTableTitleContainerWidth();
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.notesList) { return 1; }
    return Math.ceil(this.notesList.count / this.pageSizeEventNotesSubList);
  }

  public openAddNoteModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(AddEventNoteModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.addedCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
      this.loadNotes();
    };
  }

  public openEditNoteModal(note: EventNote) {
    // Create a copy of the object so it is only updated in the table after it
    // is saved to the API.
    const noteInput: EventNote = {...note};

    this.resultCallback(null, false);

    const modalRef = this.modalService.open(EditEventNoteModalComponent);
    modalRef.componentInstance.note = noteInput;
    modalRef.componentInstance.addedCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
      this.loadNotes();
    };
  }

  private loadNotes() {
    this.loadingNotes = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.eventService
      .getEventNotes(
        this.pageNumber,
        this.pageSizeEventNotesSubList,
        {event_id: this.event.event_id}
      )
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.notesList = response;
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingNotes = false;
        }
      });
  }

  private updateTableTitleContainerWidth() {
    this.notesTableTitleContainer.nativeElement.style.width =
      this.notesTableWrapper.nativeElement.offsetWidth.toString() + 'px';
  }
}
